/**
 * The viewer page (R235, docs/specs/cloud/document-viewer.md): a document on Teslyn Cloud's own address, with its
 * name, Download and Close. It is reached by a link, after the sign-in and its gates, in a tab of its own.
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { documentShape, pdfViewFor, type DocumentRef, type PdfBrowser } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { Loading } from '../components/States.tsx';
import type { Tenant } from '../tenant/tenant.ts';
import { DocumentGone, fetchDocument, findDocument } from './api.ts';
import { closeDocument } from './open.ts';
import { PdfPages, type OpenPdf } from './PdfPages.tsx';

export const DOCUMENT_GONE_WORDS =
  'This document is not available to you now. It may have been withdrawn or replaced by a newer copy. Close this ' +
  'tab and open it again from Teslyn Cloud.';
const LINK_WORDS = 'This link is not complete. Close this tab and open the document again from Teslyn Cloud.';
const CANNOT_SHOW_WORDS = 'This device cannot show the document here. Use Download to read it.';
const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

interface Loaded {
  readonly name: string;
  readonly blob: Blob;
  readonly url: string;
  readonly shape: 'pdf' | 'image' | 'other';
}

export interface DocumentPageProps {
  readonly sb: SupabaseClient;
  /** The document the address names; null for a link that is not complete. */
  readonly documentRef: DocumentRef | null;
  readonly tenant: Pick<Tenant, 'logo' | 'name'>;
  /** Tests pass their own. */
  readonly browser?: PdfBrowser;
  readonly openPdf?: OpenPdf;
  readonly close?: () => void;
}

export function DocumentPage({
  sb,
  documentRef,
  tenant,
  browser = window.navigator,
  openPdf,
  close = closeDocument,
}: DocumentPageProps): ReactElement {
  const [doc, setDoc] = useState<Loaded | null>(null);
  const [problem, setProblem] = useState<{ readonly words: string; readonly retry: boolean } | null>(
    documentRef ? null : { words: LINK_WORDS, retry: false },
  );
  const [cannotShow, setCannotShow] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!documentRef) return undefined;
    let live = true;
    let url: string | null = null;
    setProblem(null);
    void (async () => {
      try {
        const file = await findDocument(sb, documentRef);
        if (!file) throw new DocumentGone();
        const stored = await fetchDocument(sb, file);
        const shape = documentShape(file.name, stored.type);
        // the browser's viewer and the saved file go by the type, which a stored file does not always carry
        const blob = shape === 'pdf' ? stored.slice(0, stored.size, 'application/pdf') : stored;
        if (!live) return;
        url = URL.createObjectURL(blob);
        setDoc({ name: file.name, blob, url, shape });
      } catch (e) {
        if (!live) return;
        setDoc(null);
        setProblem(
          e instanceof DocumentGone
            ? { words: DOCUMENT_GONE_WORDS, retry: false }
            : { words: `The document could not be fetched. ${message(e)}`, retry: true },
        );
      }
    })();
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [sb, documentRef, attempt]);

  // after the portal has set the tab's title from the tenant
  useEffect(() => {
    if (doc) document.title = doc.name;
  }, [doc, tenant]);

  return (
    <div className="docview">
      <header className="docview__bar">
        {tenant.logo && <img className="docview__logo" src={tenant.logo} alt={tenant.name} />}
        <h1 className="docview__name heading">{doc?.name ?? 'Document'}</h1>
        {doc && (
          <a className="btn btn--primary" href={doc.url} download={doc.name}>
            Download
          </a>
        )}
        <Button onClick={close}>Close</Button>
      </header>
      <main className="docview__body">
        {problem ? (
          <div className="docview__words panel stack" role="alert">
            <p className="body">{problem.words}</p>
            {problem.retry && <Button onClick={() => setAttempt((n) => n + 1)}>Try again</Button>}
          </div>
        ) : !doc ? (
          <Loading what="the document" />
        ) : doc.shape === 'image' ? (
          <img className="docview__image" src={doc.url} alt={doc.name} />
        ) : doc.shape === 'other' || cannotShow ? (
          <div className="docview__words panel" role="status">
            <p className="body">{CANNOT_SHOW_WORDS}</p>
          </div>
        ) : pdfViewFor(browser) === 'browser' ? (
          <iframe className="docview__frame" src={doc.url} title={doc.name} />
        ) : (
          <PdfPages
            blob={doc.blob}
            onFail={() => setCannotShow(true)}
            {...(openPdf ? { open: openPdf } : {})}
          />
        )}
      </main>
    </div>
  );
}
